/*
 * AUTO-GENERATED by scripts/generate-openapi-client.cjs.
 * Source SHA-256: 55e8602c35f305dfcf55790721cd958c895be5e12c8ba4223cf299544c0a29b6
 * Do not edit manually; run npm run api:generate.
 */

import type { ApiOperations, OperationDefinition, OperationId } from './operations';

export type OperationPathParams<Id extends OperationId> = ApiOperations[Id]['pathParams'];
export type OperationQuery<Id extends OperationId> = ApiOperations[Id]['query'];
export type OperationHeaders<Id extends OperationId> = ApiOperations[Id]['headers'];
export type OperationRequestBody<Id extends OperationId> = ApiOperations[Id]['requestBody'];
export type OperationResponses<Id extends OperationId> = ApiOperations[Id]['responses'];
export type OperationResponseStatus<Id extends OperationId> = keyof OperationResponses<Id>;
type SuccessStatus<Id extends OperationId> = Extract<OperationResponseStatus<Id>, `2${string}`>;
type ErrorStatus<Id extends OperationId> = Exclude<OperationResponseStatus<Id>, SuccessStatus<Id>>;
export type OperationSuccessResponse<Id extends OperationId> = OperationResponses<Id>[SuccessStatus<Id>];
export type OperationErrorResponse<Id extends OperationId> = OperationResponses<Id>[ErrorStatus<Id>];

type RequiredKeys<Value> = {
  [Key in keyof Value]-?: Record<string, never> extends Pick<Value, Key> ? never : Key;
}[keyof Value];
type ParameterPart<Key extends string, Value> = keyof Value extends never
  ? Record<never, never>
  : RequiredKeys<Value> extends never
    ? { [Part in Key]?: Value }
    : { [Part in Key]: Value };
type BodyPart<Id extends OperationId> = [OperationRequestBody<Id>] extends [never]
  ? Record<never, never>
  : ApiOperations[Id]['requestBodyRequired'] extends true
    ? { body: OperationRequestBody<Id> }
    : { body?: OperationRequestBody<Id> };

export type OperationRequest<Id extends OperationId> =
  & ParameterPart<'pathParams', OperationPathParams<Id>>
  & ParameterPart<'query', OperationQuery<Id>>
  & ParameterPart<'headers', OperationHeaders<Id>>
  & BodyPart<Id>;

type ApiOperationTable = typeof import('./operations').API_OPERATIONS;

let pendingOperations: Promise<ApiOperationTable> | null = null;
let loadedOperations: ApiOperationTable | null = null;

/**
 * Every operation in the API, with its method, path and media types.
 *
 * It is fetched the first time something is asked of the server rather than
 * imported outright, because it is by far the largest thing this client owns
 * -- one entry per endpoint -- and a browser showing the sign-in screen has
 * called none of them. Imported outright it sat in the initial bundle, where
 * every first visit paid for all of it before asking the server anything.
 */
export function loadApiOperations(): Promise<ApiOperationTable> {
  pendingOperations ??= import('./operations').then((module) => {
    loadedOperations = module.API_OPERATIONS;
    return module.API_OPERATIONS;
  });
  return pendingOperations;
}

/**
 * Runs `use` with one operation's definition.
 *
 * Once the table is in memory it is handed over in the same tick as the call,
 * so a request still leaves in the turn that asked for it -- which is what a
 * caller that flushes a test backend, or cancels on the next line, is entitled
 * to expect. Only the first request of a session waits for anything.
 */
export function withOperation<Id extends OperationId, Result>(
  operationId: Id,
  use: (definition: OperationDefinition<Id>) => Promise<Result>,
): Promise<Result> {
  if (loadedOperations) {
    return use(loadedOperations[operationId]);
  }
  return loadApiOperations().then((operations) => use(operations[operationId]));
}

export interface GeneratedApiTransport {
  request<Id extends OperationId>(
    operationId: Id,
    definition: OperationDefinition<Id>,
    request: OperationRequest<Id>,
  ): Promise<OperationSuccessResponse<Id>>;
}

export class GeneratedApiClient {
  constructor(private readonly transport: GeneratedApiTransport) {}

  request<Id extends OperationId>(
    operationId: Id,
    request: OperationRequest<Id>,
  ): Promise<OperationSuccessResponse<Id>> {
    return withOperation(operationId, (definition) =>
      this.transport.request(operationId, definition, request));
  }
}

/** Fills one operation's path template. The caller already holds the definition. */
export function expandOperationPath(
  operationId: string,
  path: string,
  pathParams: unknown,
): string {
  const parameters = (pathParams ?? {}) as Record<string, unknown>;
  return path.replace(/\{([^}]+)\}/g, (_, name: string) => {
    const value = parameters[name];
    if (value === undefined || value === null || value === '') {
      throw new Error(`Missing path parameter "${name}" for operation "${operationId}".`);
    }
    return encodeURIComponent(String(value));
  });
}

export function buildOperationPath<Id extends OperationId>(
  operationId: Id,
  pathParams: OperationPathParams<Id>,
): Promise<string> {
  return withOperation(operationId, async (definition) =>
    expandOperationPath(operationId, definition.path, pathParams));
}

export function buildOperationQuery<Id extends OperationId>(query: OperationQuery<Id>): string {
  const search = new URLSearchParams();
  for (const [name, rawValue] of Object.entries(query as Record<string, unknown>)) {
    if (rawValue === undefined || rawValue === null || rawValue === '') {
      continue;
    }
    const values = Array.isArray(rawValue) ? rawValue : [rawValue];
    for (const value of values) {
      search.append(name, String(value));
    }
  }
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}
